import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, BarChart3, Loader2, DollarSign, TrendingUp, TrendingDown,
  CalendarDays, ClipboardList, ShoppingBag, Clock, Users, CreditCard,
  Download, Filter, Printer, PieChart, Activity, Award, Utensils
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import type { Tables } from "@/integrations/supabase/types";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  LineChart, Line, PieChart as RechartsPieChart, Pie, Cell, Legend, AreaChart, Area
} from "recharts";

type Order = Tables<"orders">;
type OrderItem = Tables<"order_items">;

const formatCurrency = (v: number) => `R$ ${v.toFixed(2).replace(".", ",")}`;
const formatPct = (v: number) => `${v.toFixed(1)}%`;

const COLORS = [
  "hsl(var(--primary))",
  "hsl(var(--chart-2))",
  "hsl(var(--chart-3))",
  "hsl(var(--chart-4))",
  "hsl(var(--chart-5))",
  "hsl(25, 95%, 53%)",
  "hsl(280, 65%, 60%)",
  "hsl(180, 60%, 45%)",
];

const ORDER_TYPE_LABELS: Record<string, string> = {
  dine_in: "Salão",
  takeout: "Retirada",
  delivery: "Delivery",
  scheduled: "Agendado",
};

const PAYMENT_LABELS: Record<string, string> = {
  pix: "PIX",
  credit_card: "Cartão de Crédito",
  debit_card: "Cartão de Débito",
  cash: "Dinheiro",
  mercadopago: "Mercado Pago",
};

const STATUS_LABELS: Record<string, string> = {
  received: "Recebido",
  preparing: "Em Preparo",
  ready: "Pronto",
  out_for_delivery: "Em Entrega",
  completed: "Finalizado",
  cancelled: "Cancelado",
};

const Relatorios = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [restaurantId, setRestaurantId] = useState<string | null>(null);
  const [restaurantName, setRestaurantName] = useState("");
  const [orders, setOrders] = useState<Order[]>([]);
  const [prevOrders, setPrevOrders] = useState<Order[]>([]);
  const [orderItems, setOrderItems] = useState<OrderItem[]>([]);
  const [ratings, setRatings] = useState<any[]>([]);

  // Filters
  const [period, setPeriod] = useState("7d");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const getDateRange = useCallback(() => {
    const now = new Date();
    let from: Date;
    const to = new Date(now);
    to.setHours(23, 59, 59, 999);

    if (period === "custom" && dateFrom && dateTo) {
      return { from: new Date(dateFrom + "T00:00:00"), to: new Date(dateTo + "T23:59:59") };
    }

    switch (period) {
      case "today":
        from = new Date(now);
        from.setHours(0, 0, 0, 0);
        break;
      case "yesterday":
        from = new Date(now);
        from.setDate(from.getDate() - 1);
        from.setHours(0, 0, 0, 0);
        to.setDate(to.getDate() - 1);
        break;
      case "7d":
        from = new Date(now);
        from.setDate(from.getDate() - 7);
        break;
      case "15d":
        from = new Date(now);
        from.setDate(from.getDate() - 15);
        break;
      case "30d":
        from = new Date(now);
        from.setDate(from.getDate() - 30);
        break;
      case "90d":
        from = new Date(now);
        from.setDate(from.getDate() - 90);
        break;
      default:
        from = new Date(now);
        from.setDate(from.getDate() - 7);
    }
    return { from, to };
  }, [period, dateFrom, dateTo]);

  const fetchData = useCallback(async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { navigate("/login"); return; }

    const impersonateId = sessionStorage.getItem("admin_impersonate_restaurant_id");
    let rest: any = null;
    
    if (impersonateId) {
      const { data } = await supabase
        .from("restaurants").select("id, name").eq("id", impersonateId).maybeSingle();
      rest = data;
    } else {
      const { data } = await supabase
        .from("restaurants").select("id, name").eq("owner_id", user.id).maybeSingle();
      rest = data;
    }
    
    if (!rest) { navigate("/dashboard"); return; }

    setRestaurantId(rest.id);
    setRestaurantName(rest.name);

    const { from, to } = getDateRange();
    const periodMs = to.getTime() - from.getTime();
    const prevFrom = new Date(from.getTime() - periodMs);
    const prevTo = new Date(from.getTime() - 1);

    const [ordersRes, ratingsRes, prevOrdersRes] = await Promise.all([
      supabase
        .from("orders").select("*")
        .eq("restaurant_id", rest.id)
        .gte("created_at", from.toISOString())
        .lte("created_at", to.toISOString())
        .order("created_at", { ascending: true }),
      supabase
        .from("order_ratings").select("*")
        .eq("restaurant_id", rest.id)
        .gte("created_at", from.toISOString())
        .lte("created_at", to.toISOString()),
      supabase
        .from("orders").select("*")
        .eq("restaurant_id", rest.id)
        .gte("created_at", prevFrom.toISOString())
        .lte("created_at", prevTo.toISOString()),
    ]);

    const allOrders = ordersRes.data || [];
    setOrders(allOrders);
    setPrevOrders(prevOrdersRes.data || []);
    setRatings((ratingsRes.data as any[]) || []);

    const orderIds = allOrders.map((o) => o.id);
    if (orderIds.length > 0) {
      // Fetch in chunks of 100 to avoid URL limits
      const chunks: string[][] = [];
      for (let i = 0; i < orderIds.length; i += 100) {
        chunks.push(orderIds.slice(i, i + 100));
      }
      const allItems: OrderItem[] = [];
      for (const chunk of chunks) {
        const { data } = await supabase.from("order_items").select("*").in("order_id", chunk);
        if (data) allItems.push(...data);
      }
      setOrderItems(allItems);
    } else {
      setOrderItems([]);
    }

    setLoading(false);
  }, [navigate, getDateRange]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ===== COMPUTED DATA =====
  const completedOrders = useMemo(() => orders.filter((o) => o.status !== "cancelled"), [orders]);
  const cancelledOrders = useMemo(() => orders.filter((o) => o.status === "cancelled"), [orders]);

  // KPIs
  const kpis = useMemo(() => {
    const revenue = completedOrders.reduce((s, o) => s + Number(o.total), 0);
    const avgTicket = completedOrders.length > 0 ? revenue / completedOrders.length : 0;
    const deliveryRevenue = completedOrders.filter(o => o.order_type === "delivery").reduce((s, o) => s + Number(o.delivery_fee), 0);
    const discounts = completedOrders.reduce((s, o) => s + Number(o.discount), 0);
    const cancelRate = orders.length > 0 ? (cancelledOrders.length / orders.length) * 100 : 0;

    return {
      totalOrders: orders.length,
      completedOrders: completedOrders.length,
      cancelledOrders: cancelledOrders.length,
      revenue,
      avgTicket,
      deliveryRevenue,
      discounts,
      cancelRate,
    };
  }, [orders, completedOrders, cancelledOrders]);

  // Previous period KPIs
  const prevKpis = useMemo(() => {
    const prevCompleted = prevOrders.filter((o) => o.status !== "cancelled");
    const prevCancelled = prevOrders.filter((o) => o.status === "cancelled");
    const revenue = prevCompleted.reduce((s, o) => s + Number(o.total), 0);
    const avgTicket = prevCompleted.length > 0 ? revenue / prevCompleted.length : 0;
    const cancelRate = prevOrders.length > 0 ? (prevCancelled.length / prevOrders.length) * 100 : 0;
    return { revenue, completedOrders: prevCompleted.length, avgTicket, cancelRate };
  }, [prevOrders]);

  const calcVariation = (current: number, previous: number) => {
    if (previous === 0) return current > 0 ? 100 : 0;
    return ((current - previous) / previous) * 100;
  };

  const variations = useMemo(() => ({
    revenue: calcVariation(kpis.revenue, prevKpis.revenue),
    orders: calcVariation(kpis.completedOrders, prevKpis.completedOrders),
    avgTicket: calcVariation(kpis.avgTicket, prevKpis.avgTicket),
    cancelRate: kpis.cancelRate - prevKpis.cancelRate,
  }), [kpis, prevKpis]);

  // Top customers
  const topCustomers = useMemo(() => {
    const map = new Map<string, { name: string; phone: string; orders: number; revenue: number; avgTicket: number; lastOrder: string }>();
    completedOrders.forEach((o) => {
      const name = o.customer_name?.trim() || "";
      const phone = o.customer_phone?.trim() || "";
      if (!name && !phone) return;
      const key = phone || name;
      const existing = map.get(key) || { name, phone, orders: 0, revenue: 0, avgTicket: 0, lastOrder: "" };
      if (name && !existing.name) existing.name = name;
      if (phone && !existing.phone) existing.phone = phone;
      existing.orders += 1;
      existing.revenue += Number(o.total);
      if (o.created_at > existing.lastOrder) existing.lastOrder = o.created_at;
      map.set(key, existing);
    });
    const list = Array.from(map.values());
    list.forEach((c) => { c.avgTicket = c.orders > 0 ? c.revenue / c.orders : 0; });
    return {
      byFrequency: [...list].sort((a, b) => b.orders - a.orders).slice(0, 15),
      byRevenue: [...list].sort((a, b) => b.revenue - a.revenue).slice(0, 15),
      totalUnique: list.length,
    };
  }, [completedOrders]);

  const revenueByDay = useMemo(() => {
    const map = new Map<string, { date: string; label: string; revenue: number; orders: number }>();
    completedOrders.forEach((o) => {
      const key = o.created_at.slice(0, 10);
      const existing = map.get(key) || {
        date: key,
        label: new Date(key + "T12:00:00").toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" }),
        revenue: 0,
        orders: 0,
      };
      existing.revenue += Number(o.total);
      existing.orders += 1;
      map.set(key, existing);
    });
    return Array.from(map.values()).sort((a, b) => a.date.localeCompare(b.date));
  }, [completedOrders]);

  // Revenue by hour
  const revenueByHour = useMemo(() => {
    const hours = Array.from({ length: 24 }, (_, i) => ({ hour: i, label: `${String(i).padStart(2, "0")}h`, orders: 0, revenue: 0 }));
    completedOrders.forEach((o) => {
      const h = new Date(o.created_at).getHours();
      hours[h].orders += 1;
      hours[h].revenue += Number(o.total);
    });
    return hours.filter((h) => h.orders > 0);
  }, [completedOrders]);

  // Revenue by weekday
  const revenueByWeekday = useMemo(() => {
    const days = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
    const data = days.map((d, i) => ({ day: d, dayIndex: i, orders: 0, revenue: 0, avgTicket: 0 }));
    completedOrders.forEach((o) => {
      const d = new Date(o.created_at).getDay();
      data[d].orders += 1;
      data[d].revenue += Number(o.total);
    });
    data.forEach((d) => { d.avgTicket = d.orders > 0 ? d.revenue / d.orders : 0; });
    return data;
  }, [completedOrders]);

  // Orders by type
  const ordersByType = useMemo(() => {
    const map = new Map<string, { type: string; label: string; count: number; revenue: number }>();
    completedOrders.forEach((o) => {
      const existing = map.get(o.order_type) || {
        type: o.order_type,
        label: ORDER_TYPE_LABELS[o.order_type] || o.order_type,
        count: 0,
        revenue: 0,
      };
      existing.count += 1;
      existing.revenue += Number(o.total);
      map.set(o.order_type, existing);
    });
    return Array.from(map.values()).sort((a, b) => b.count - a.count);
  }, [completedOrders]);

  // Orders by payment method
  const ordersByPayment = useMemo(() => {
    const map = new Map<string, { method: string; label: string; count: number; revenue: number }>();
    completedOrders.forEach((o) => {
      const method = o.payment_method || "not_set";
      const existing = map.get(method) || {
        method,
        label: PAYMENT_LABELS[method] || "Não informado",
        count: 0,
        revenue: 0,
      };
      existing.count += 1;
      existing.revenue += Number(o.total);
      map.set(method, existing);
    });
    return Array.from(map.values()).sort((a, b) => b.revenue - a.revenue);
  }, [completedOrders]);

  // Top products
  const topProducts = useMemo(() => {
    const map = new Map<string, { name: string; qty: number; revenue: number }>();
    orderItems.forEach((item) => {
      const existing = map.get(item.product_name) || { name: item.product_name, qty: 0, revenue: 0 };
      existing.qty += item.quantity;
      existing.revenue += Number(item.total_price);
      map.set(item.product_name, existing);
    });
    return Array.from(map.values()).sort((a, b) => b.qty - a.qty).slice(0, 10);
  }, [orderItems]);

  // Top products by revenue
  const topProductsByRevenue = useMemo(() => {
    return [...topProducts].sort((a, b) => b.revenue - a.revenue).slice(0, 10);
  }, [topProducts]);

  // Orders by status
  const ordersByStatus = useMemo(() => {
    const map = new Map<string, number>();
    orders.forEach((o) => { map.set(o.status, (map.get(o.status) || 0) + 1); });
    return Array.from(map.entries()).map(([status, count]) => ({
      status,
      label: STATUS_LABELS[status] || status,
      count,
    }));
  }, [orders]);

  // Ratings
  const ratingsStats = useMemo(() => {
    if (ratings.length === 0) return null;
    const avgFood = ratings.reduce((s, r) => s + Number(r.food_rating), 0) / ratings.length;
    const avgService = ratings.reduce((s, r) => s + Number(r.service_rating), 0) / ratings.length;
    return { avgFood, avgService, avgOverall: (avgFood + avgService) / 2, total: ratings.length };
  }, [ratings]);

  // Tables performance
  const tablePerformance = useMemo(() => {
    const map = new Map<number, { table: number; orders: number; revenue: number }>();
    completedOrders
      .filter((o) => o.order_type === "dine_in" && o.table_number)
      .forEach((o) => {
        const t = o.table_number!;
        const existing = map.get(t) || { table: t, orders: 0, revenue: 0 };
        existing.orders += 1;
        existing.revenue += Number(o.total);
        map.set(t, existing);
      });
    return Array.from(map.values()).sort((a, b) => b.revenue - a.revenue);
  }, [completedOrders]);

  // Heatmap: orders by hour x weekday
  const heatmapData = useMemo(() => {
    const DAYS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
    const grid: number[][] = Array.from({ length: 7 }, () => Array(24).fill(0));
    let maxVal = 0;
    completedOrders.forEach((o) => {
      const d = new Date(o.created_at);
      grid[d.getDay()][d.getHours()] += 1;
    });
    grid.forEach((row) => row.forEach((v) => { if (v > maxVal) maxVal = v; }));
    return { grid, days: DAYS, maxVal };
  }, [completedOrders]);

  // CSV export
  const exportCSV = () => {
    const headers = ["Data", "Pedido", "Tipo", "Mesa", "Subtotal", "Desconto", "Taxa Entrega", "Total", "Status", "Pagamento"];
    const rows = orders.map((o) => [
      new Date(o.created_at).toLocaleString("pt-BR"),
      o.id.slice(0, 8),
      ORDER_TYPE_LABELS[o.order_type] || o.order_type,
      o.table_number || "",
      Number(o.subtotal).toFixed(2),
      Number(o.discount).toFixed(2),
      Number(o.delivery_fee).toFixed(2),
      Number(o.total).toFixed(2),
      STATUS_LABELS[o.status] || o.status,
      PAYMENT_LABELS[o.payment_method || ""] || "N/A",
    ]);

    const csv = [headers.join(";"), ...rows.map((r) => r.join(";"))].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `relatorio-${period}-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    toast({ title: "Relatório exportado com sucesso!" });
  };

  const tooltipStyle = {
    background: "hsl(var(--card))",
    border: "1px solid hsl(var(--border))",
    borderRadius: 8,
    fontSize: 12,
    color: "hsl(var(--foreground))",
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b border-border">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold text-foreground flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-primary" />
                Relatórios
              </h1>
              <p className="text-xs text-muted-foreground">{restaurantName}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={exportCSV}>
              <Download className="h-4 w-4 mr-2" />
              Exportar CSV
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Period filter */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-3 items-end">
              <div className="flex-1 space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">Período</label>
                <Select value={period} onValueChange={setPeriod}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="today">Hoje</SelectItem>
                    <SelectItem value="yesterday">Ontem</SelectItem>
                    <SelectItem value="7d">Últimos 7 dias</SelectItem>
                    <SelectItem value="15d">Últimos 15 dias</SelectItem>
                    <SelectItem value="30d">Últimos 30 dias</SelectItem>
                    <SelectItem value="90d">Últimos 90 dias</SelectItem>
                    <SelectItem value="custom">Personalizado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {period === "custom" && (
                <>
                  <div className="flex-1 space-y-1.5">
                    <label className="text-xs font-medium text-muted-foreground">De</label>
                    <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
                  </div>
                  <div className="flex-1 space-y-1.5">
                    <label className="text-xs font-medium text-muted-foreground">Até</label>
                    <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
                  </div>
                </>
              )}
              <Button size="sm" onClick={fetchData} className="h-10">
                <Filter className="h-4 w-4 mr-2" />
                Aplicar
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: "Faturamento", value: formatCurrency(kpis.revenue), icon: DollarSign, accent: "text-success", variation: variations.revenue, invertColor: false },
            { label: "Pedidos", value: String(kpis.completedOrders), icon: ClipboardList, accent: "text-primary", variation: variations.orders, invertColor: false },
            { label: "Ticket Médio", value: formatCurrency(kpis.avgTicket), icon: TrendingUp, accent: "text-foreground", variation: variations.avgTicket, invertColor: false },
            { label: "Taxa Cancelamento", value: formatPct(kpis.cancelRate), icon: TrendingDown, accent: kpis.cancelRate > 10 ? "text-destructive" : "text-success", variation: variations.cancelRate, invertColor: true },
          ].map((s) => {
            const isPositive = s.invertColor ? s.variation < 0 : s.variation > 0;
            const isNegative = s.invertColor ? s.variation > 0 : s.variation < 0;
            const variationColor = isPositive ? "text-green-600" : isNegative ? "text-destructive" : "text-muted-foreground";
            const VariationIcon = s.variation > 0 ? TrendingUp : s.variation < 0 ? TrendingDown : null;
            return (
              <Card key={s.label}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <s.icon className={`h-4 w-4 ${s.accent}`} />
                    <span className="text-xs text-muted-foreground">{s.label}</span>
                  </div>
                  <p className={`text-2xl font-bold ${s.accent}`}>{s.value}</p>
                  {(prevKpis.revenue > 0 || prevKpis.completedOrders > 0) && (
                    <div className={`flex items-center gap-1 mt-1.5 ${variationColor}`}>
                      {VariationIcon && <VariationIcon className="h-3 w-3" />}
                      <span className="text-xs font-medium">
                        {s.variation > 0 ? "+" : ""}{s.variation.toFixed(1)}%
                      </span>
                      <span className="text-[10px] text-muted-foreground ml-0.5">vs período anterior</span>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Sub KPIs */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-3 text-center">
              <p className="text-lg font-bold text-foreground">{formatCurrency(kpis.discounts)}</p>
              <p className="text-[11px] text-muted-foreground">Descontos Aplicados</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3 text-center">
              <p className="text-lg font-bold text-foreground">{formatCurrency(kpis.deliveryRevenue)}</p>
              <p className="text-[11px] text-muted-foreground">Taxas de Entrega</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3 text-center">
              <p className="text-lg font-bold text-foreground">{kpis.cancelledOrders}</p>
              <p className="text-[11px] text-muted-foreground">Pedidos Cancelados</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3 text-center">
              <p className="text-lg font-bold text-foreground">{ratingsStats ? `${ratingsStats.avgOverall.toFixed(1)} ★` : "N/A"}</p>
              <p className="text-[11px] text-muted-foreground">Avaliação Média ({ratingsStats?.total || 0})</p>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="vendas">
          <TabsList className="w-full sm:w-auto flex-wrap">
            <TabsTrigger value="vendas" className="flex items-center gap-1.5">
              <DollarSign className="h-3.5 w-3.5" />
              Vendas
            </TabsTrigger>
            <TabsTrigger value="produtos" className="flex items-center gap-1.5">
              <ShoppingBag className="h-3.5 w-3.5" />
              Produtos
            </TabsTrigger>
            <TabsTrigger value="operacional" className="flex items-center gap-1.5">
              <Activity className="h-3.5 w-3.5" />
              Operacional
            </TabsTrigger>
            <TabsTrigger value="pagamentos" className="flex items-center gap-1.5">
              <CreditCard className="h-3.5 w-3.5" />
              Pagamentos
            </TabsTrigger>
            <TabsTrigger value="clientes" className="flex items-center gap-1.5">
              <Users className="h-3.5 w-3.5" />
              Clientes
            </TabsTrigger>
          </TabsList>

          {/* ===== VENDAS TAB ===== */}
          <TabsContent value="vendas" className="space-y-6 mt-4">
            {/* Revenue over time */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-primary" />
                  Faturamento por Dia
                </CardTitle>
              </CardHeader>
              <CardContent>
                {revenueByDay.length > 0 ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <AreaChart data={revenueByDay}>
                      <defs>
                        <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="label" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                      <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickFormatter={(v) => `R$${v}`} />
                      <Tooltip contentStyle={tooltipStyle} formatter={(value: number) => [formatCurrency(value), "Faturamento"]} />
                      <Area type="monotone" dataKey="revenue" stroke="hsl(var(--primary))" fill="url(#colorRevenue)" strokeWidth={2} />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-[200px] text-muted-foreground text-sm">
                    Sem dados no período selecionado
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Revenue by weekday */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <CalendarDays className="h-4 w-4 text-primary" />
                  Faturamento por Dia da Semana
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={revenueByWeekday} barSize={36}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="day" tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} />
                    <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickFormatter={(v) => `R$${v}`} />
                    <Tooltip contentStyle={tooltipStyle} formatter={(value: number, name: string) => [
                      name === "revenue" ? formatCurrency(value) : value,
                      name === "revenue" ? "Faturamento" : "Pedidos"
                    ]} />
                    <Bar dataKey="revenue" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Orders by type */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Utensils className="h-4 w-4 text-primary" />
                    Pedidos por Tipo
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {ordersByType.length > 0 ? (
                    <ResponsiveContainer width="100%" height={250}>
                      <RechartsPieChart>
                        <Pie
                          data={ordersByType}
                          dataKey="count"
                          nameKey="label"
                          cx="50%"
                          cy="50%"
                          outerRadius={90}
                          label={({ label, percent }) => `${label} ${(percent * 100).toFixed(0)}%`}
                        >
                          {ordersByType.map((_, i) => (
                            <Cell key={i} fill={COLORS[i % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip contentStyle={tooltipStyle} formatter={(value: number) => [value, "Pedidos"]} />
                      </RechartsPieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-[200px] text-muted-foreground text-sm">Sem dados</div>
                  )}
                </CardContent>
              </Card>

              {/* Revenue by type table */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Detalhamento por Tipo</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {ordersByType.map((t, i) => (
                      <div key={t.type} className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-3 h-3 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />
                          <div>
                            <p className="text-sm font-medium text-foreground">{t.label}</p>
                            <p className="text-xs text-muted-foreground">{t.count} pedido{t.count !== 1 ? "s" : ""}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-semibold text-foreground">{formatCurrency(t.revenue)}</p>
                          <p className="text-xs text-muted-foreground">
                            {kpis.revenue > 0 ? formatPct((t.revenue / kpis.revenue) * 100) : "0%"}
                          </p>
                        </div>
                      </div>
                    ))}
                    {ordersByType.length === 0 && (
                      <p className="text-sm text-muted-foreground text-center py-8">Sem dados</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* ===== PRODUTOS TAB ===== */}
          <TabsContent value="produtos" className="space-y-6 mt-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Top by qty */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Award className="h-4 w-4 text-primary" />
                    Mais Vendidos (Quantidade)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {topProducts.length > 0 ? (
                    <div className="space-y-3">
                      {topProducts.map((p, i) => (
                        <div key={p.name} className="flex items-center gap-3">
                          <span className="text-xs font-bold text-muted-foreground w-5">{i + 1}º</span>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate text-foreground">{p.name}</p>
                            <div className="w-full bg-muted rounded-full h-1.5 mt-1">
                              <div
                                className="h-1.5 rounded-full bg-primary transition-all"
                                style={{ width: `${(p.qty / topProducts[0].qty) * 100}%` }}
                              />
                            </div>
                          </div>
                          <div className="text-right shrink-0">
                            <p className="text-sm font-semibold text-foreground">{p.qty} un</p>
                            <p className="text-[10px] text-muted-foreground">{formatCurrency(p.revenue)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-8">Sem dados</p>
                  )}
                </CardContent>
              </Card>

              {/* Top by revenue */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <DollarSign className="h-4 w-4 text-primary" />
                    Mais Vendidos (Faturamento)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {topProductsByRevenue.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={topProductsByRevenue} layout="vertical" barSize={16}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis type="number" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickFormatter={(v) => `R$${v}`} />
                        <YAxis dataKey="name" type="category" width={120} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                        <Tooltip contentStyle={tooltipStyle} formatter={(value: number) => [formatCurrency(value), "Faturamento"]} />
                        <Bar dataKey="revenue" fill="hsl(var(--primary))" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-8">Sem dados</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* ===== OPERACIONAL TAB ===== */}
          <TabsContent value="operacional" className="space-y-6 mt-4">
            {/* Orders by hour */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Clock className="h-4 w-4 text-primary" />
                  Pedidos por Horário
                </CardTitle>
              </CardHeader>
              <CardContent>
                {revenueByHour.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={revenueByHour} barSize={20}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="label" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                      <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                      <Tooltip contentStyle={tooltipStyle} formatter={(value: number, name: string) => [
                        name === "orders" ? value : formatCurrency(value),
                        name === "orders" ? "Pedidos" : "Faturamento"
                      ]} />
                      <Bar dataKey="orders" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-[200px] text-muted-foreground text-sm">Sem dados</div>
                )}
              </CardContent>
            </Card>

            {/* Heatmap */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Activity className="h-4 w-4 text-primary" />
                  Mapa de Calor — Horários de Pico
                </CardTitle>
              </CardHeader>
              <CardContent>
                {heatmapData.maxVal > 0 ? (
                  <div className="overflow-x-auto">
                    <div className="min-w-[600px]">
                      {/* Hour headers */}
                      <div className="flex items-center mb-1">
                        <div className="w-10 shrink-0" />
                        {Array.from({ length: 24 }, (_, h) => (
                          <div key={h} className="flex-1 text-center text-[10px] text-muted-foreground font-medium">
                            {String(h).padStart(2, "0")}
                          </div>
                        ))}
                      </div>
                      {/* Rows */}
                      {heatmapData.days.map((day, di) => (
                        <div key={day} className="flex items-center gap-0 mb-[2px]">
                          <div className="w-10 shrink-0 text-xs font-medium text-muted-foreground">{day}</div>
                          {heatmapData.grid[di].map((count, hi) => {
                            const intensity = heatmapData.maxVal > 0 ? count / heatmapData.maxVal : 0;
                            return (
                              <div
                                key={hi}
                                className="flex-1 aspect-square rounded-sm border border-border/30 relative group cursor-default"
                                style={{
                                  backgroundColor: count === 0
                                    ? "hsl(var(--muted))"
                                    : `hsl(var(--primary) / ${0.15 + intensity * 0.85})`,
                                }}
                                title={`${day} ${String(hi).padStart(2, "0")}h — ${count} pedido${count !== 1 ? "s" : ""}`}
                              >
                                {count > 0 && (
                                  <span className="absolute inset-0 flex items-center justify-center text-[9px] font-bold text-primary-foreground opacity-0 group-hover:opacity-100 transition-opacity">
                                    {count}
                                  </span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ))}
                      {/* Legend */}
                      <div className="flex items-center justify-end gap-2 mt-3">
                        <span className="text-[10px] text-muted-foreground">Menos</span>
                        {[0, 0.25, 0.5, 0.75, 1].map((v) => (
                          <div
                            key={v}
                            className="w-4 h-4 rounded-sm border border-border/30"
                            style={{
                              backgroundColor: v === 0
                                ? "hsl(var(--muted))"
                                : `hsl(var(--primary) / ${0.15 + v * 0.85})`,
                            }}
                          />
                        ))}
                        <span className="text-[10px] text-muted-foreground">Mais</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-[200px] text-muted-foreground text-sm">Sem dados</div>
                )}
              </CardContent>
            </Card>

            {/* Tables + Status */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Table performance */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Utensils className="h-4 w-4 text-primary" />
                    Desempenho por Mesa
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {tablePerformance.length > 0 ? (
                    <div className="space-y-2 max-h-[300px] overflow-y-auto">
                      {tablePerformance.map((t) => (
                        <div key={t.table} className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
                          <div className="flex items-center gap-3">
                            <div className="h-8 w-8 rounded-lg bg-muted flex items-center justify-center text-sm font-bold text-foreground">
                              {t.table}
                            </div>
                            <span className="text-sm text-muted-foreground">{t.orders} pedido{t.orders !== 1 ? "s" : ""}</span>
                          </div>
                          <span className="text-sm font-semibold text-foreground">{formatCurrency(t.revenue)}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-8">Sem pedidos no salão</p>
                  )}
                </CardContent>
              </Card>

              {/* Orders by status */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <ClipboardList className="h-4 w-4 text-primary" />
                    Pedidos por Status
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {ordersByStatus.map((s) => (
                      <div key={s.status} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">{s.label}</Badge>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="w-24 bg-muted rounded-full h-2">
                            <div
                              className="h-2 rounded-full bg-primary transition-all"
                              style={{ width: `${orders.length > 0 ? (s.count / orders.length) * 100 : 0}%` }}
                            />
                          </div>
                          <span className="text-sm font-semibold text-foreground w-8 text-right">{s.count}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Ratings */}
            {ratingsStats && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    ⭐ Avaliações dos Clientes
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center">
                      <p className="text-3xl font-bold text-foreground">{ratingsStats.avgOverall.toFixed(1)}</p>
                      <p className="text-xs text-muted-foreground">Nota Geral</p>
                    </div>
                    <div className="text-center">
                      <p className="text-3xl font-bold text-foreground">{ratingsStats.avgFood.toFixed(1)}</p>
                      <p className="text-xs text-muted-foreground">Comida</p>
                    </div>
                    <div className="text-center">
                      <p className="text-3xl font-bold text-foreground">{ratingsStats.avgService.toFixed(1)}</p>
                      <p className="text-xs text-muted-foreground">Serviço</p>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground text-center mt-3">{ratingsStats.total} avaliações no período</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* ===== PAGAMENTOS TAB ===== */}
          <TabsContent value="pagamentos" className="space-y-6 mt-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Payment pie chart */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <CreditCard className="h-4 w-4 text-primary" />
                    Distribuição por Forma de Pagamento
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {ordersByPayment.length > 0 ? (
                    <ResponsiveContainer width="100%" height={280}>
                      <RechartsPieChart>
                        <Pie
                          data={ordersByPayment}
                          dataKey="revenue"
                          nameKey="label"
                          cx="50%"
                          cy="50%"
                          outerRadius={100}
                          label={({ label, percent }) => `${label} ${(percent * 100).toFixed(0)}%`}
                        >
                          {ordersByPayment.map((_, i) => (
                            <Cell key={i} fill={COLORS[i % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip contentStyle={tooltipStyle} formatter={(value: number) => [formatCurrency(value), "Faturamento"]} />
                      </RechartsPieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-[200px] text-muted-foreground text-sm">Sem dados</div>
                  )}
                </CardContent>
              </Card>

              {/* Payment detail table */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Detalhamento por Pagamento</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {ordersByPayment.map((p, i) => (
                      <div key={p.method} className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
                        <div className="flex items-center gap-3">
                          <div className="w-3 h-3 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />
                          <div>
                            <p className="text-sm font-medium text-foreground">{p.label}</p>
                            <p className="text-xs text-muted-foreground">{p.count} pedido{p.count !== 1 ? "s" : ""}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-semibold text-foreground">{formatCurrency(p.revenue)}</p>
                          <p className="text-xs text-muted-foreground">
                            {kpis.revenue > 0 ? formatPct((p.revenue / kpis.revenue) * 100) : "0%"}
                          </p>
                        </div>
                      </div>
                    ))}
                    {ordersByPayment.length === 0 && (
                      <p className="text-sm text-muted-foreground text-center py-8">Sem dados</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* ===== CLIENTES TAB ===== */}
          <TabsContent value="clientes" className="space-y-6 mt-4">
            {/* Summary */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-primary">{topCustomers.totalUnique}</p>
                  <p className="text-xs text-muted-foreground">Clientes Únicos</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-foreground">
                    {topCustomers.totalUnique > 0
                      ? (completedOrders.length / topCustomers.totalUnique).toFixed(1)
                      : "0"}
                  </p>
                  <p className="text-xs text-muted-foreground">Pedidos/Cliente</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-foreground">
                    {topCustomers.totalUnique > 0
                      ? formatCurrency(kpis.revenue / topCustomers.totalUnique)
                      : "R$ 0,00"}
                  </p>
                  <p className="text-xs text-muted-foreground">Gasto Médio/Cliente</p>
                </CardContent>
              </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* By frequency */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Award className="h-4 w-4 text-primary" />
                    Mais Frequentes
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {topCustomers.byFrequency.length > 0 ? (
                    <div className="space-y-2 max-h-[420px] overflow-y-auto">
                      {topCustomers.byFrequency.map((c, i) => (
                        <div key={i} className="flex items-center gap-3 py-2 border-b border-border/50 last:border-0">
                          <div className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                            i < 3 ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
                          }`}>
                            {i + 1}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate text-foreground">{c.name || "Sem nome"}</p>
                            <p className="text-[11px] text-muted-foreground">{c.phone || "Sem telefone"}</p>
                          </div>
                          <div className="text-right shrink-0">
                            <p className="text-sm font-bold text-foreground">{c.orders} pedido{c.orders !== 1 ? "s" : ""}</p>
                            <p className="text-[10px] text-muted-foreground">{formatCurrency(c.revenue)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-8">Sem dados de clientes no período</p>
                  )}
                </CardContent>
              </Card>

              {/* By revenue */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <DollarSign className="h-4 w-4 text-primary" />
                    Maiores Gastos
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {topCustomers.byRevenue.length > 0 ? (
                    <div className="space-y-2 max-h-[420px] overflow-y-auto">
                      {topCustomers.byRevenue.map((c, i) => (
                        <div key={i} className="flex items-center gap-3 py-2 border-b border-border/50 last:border-0">
                          <div className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                            i < 3 ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
                          }`}>
                            {i + 1}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate text-foreground">{c.name || "Sem nome"}</p>
                            <p className="text-[11px] text-muted-foreground">
                              {c.phone || "Sem telefone"} · Ticket médio: {formatCurrency(c.avgTicket)}
                            </p>
                          </div>
                          <div className="text-right shrink-0">
                            <p className="text-sm font-bold text-foreground">{formatCurrency(c.revenue)}</p>
                            <p className="text-[10px] text-muted-foreground">{c.orders} pedido{c.orders !== 1 ? "s" : ""}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-8">Sem dados de clientes no período</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Relatorios;
